import { Stack } from '@mui/material';
import { NoManagedPackageError } from '../../common/error-pages/NoManagedPackageError';
import { ActionsRequireMp } from '../../common/install-managed-package/getInstallManagedPackageText';
import { DateBy } from '../../common/datepicker/types';
import { useEffect, useRef, useState } from 'react';
import AppsConnectionDialog from './apps/AppsConnectionDialog';
import { Permission } from '@server/permissions';
import { AiAssistantPageSkeleton } from './AiAssistantPageSkeleton';
import { AiAssistantConversation, AiAssistantFilters } from './types';
import { DEFAULT_DATEPICKER_STATE } from './utils';
import { AiAssistantEmptyState } from './empty-state/AiAssistantEmptyState';
import { AiAssistantPageHeader } from './AiAssistantPageHeader';
import { AiAssistantPerformance } from './AiAssistantPerformance';
import { AiAssistantConversations } from './AiAssistantConversations';
import { useAiAssistant } from './useAiAssistant';
import { getDateFromKey } from '../../common/datepicker/getDateFromKey';
import useIsManagedPackageInstalled from '../../common/install-managed-package/useIsManagedPackageInstalled';
import { useSelector } from 'react-redux';
import { selectAiAssistantConversations } from '../../../reducers/aiSupportReducer';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { AiAssistantConversationPreview } from './AiAssistantConversationPreview';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { PAGE_SIDE_PADDING } from '../../../constants';
import { useTimeout } from '../../../hooks/useTimeout';

interface AiAssistantPageProps {
  goBackCb?: () => void;
  isCollapsed?: boolean;
  handleClosePanel?: () => void;
}

const AiAssistantPage = ({ goBackCb, isCollapsed, handleClosePanel }: AiAssistantPageProps) => {
  const { setSweepTimeout } = useTimeout();
  const { supportAgentInAgenticCenter } = useFeatureToggle();
  const {
    fetchAiAssistantConversations: fetchAiAssistentConversations,
    isLoading,
    fetchApps,
    isInitialized,
  } = useAiAssistant();
  const isManagedPackagedInstalled = useIsManagedPackageInstalled();
  const defaultCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const tableData = useSelector(selectAiAssistantConversations(defaultCrmOrgId ?? ''));

  const [isPanelVisible, setIsPanelVisible] = useState(false);

  const dateFilter = getDateFromKey(DEFAULT_DATEPICKER_STATE);
  const [dateFilters, setDateFilters] = useState<AiAssistantFilters>({
    startTime: dateFilter[DateBy.From],
    endTime: dateFilter[DateBy.To],
  });

  const [isAiAssistantDialogOpen, setAiAssistantDialogOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<AiAssistantConversation>();

  const openPreview = (conversation: AiAssistantConversation) => {
    setSelectedConversation(conversation);
  };

  const permissionString: Permission[] = ['read:ai-sessions'];
  const datePickerRef = useRef<any>(null);

  const handleClosePreview = () => {
    setIsPanelVisible(false);
    // Wait for animation to complete before removing the item
    setSweepTimeout(() => {
      setSelectedConversation(undefined);
    }, 200);
  };

  useEffect(() => {
    if (selectedConversation) {
      setIsPanelVisible(true);
    }
  }, [selectedConversation]);

  useEffect(() => {
    //assure that the data is fetched when the component is mounted or defaultCrmOrgId changes
    fetchApps(defaultCrmOrgId ?? '');
    fetchAiAssistentConversations(defaultCrmOrgId ?? '');
  }, [defaultCrmOrgId, fetchAiAssistentConversations, fetchApps]);

  const withFilters = isManagedPackagedInstalled && isInitialized && !isLoading;
  const isEmpty = !isInitialized && !isLoading;

  return (
    <Stack height="100%" sx={{ overflow: 'hidden', width: '100%', flexDirection: 'row' }}>
      {isLoading && (
        <Stack
          height="100%"
          p={supportAgentInAgenticCenter ? 2 : 4}
          pl={PAGE_SIDE_PADDING + 'px'}
          pr={PAGE_SIDE_PADDING + 'px'}
          width="100%"
        >
          <AiAssistantPageSkeleton goBackCb={goBackCb} />
        </Stack>
      )}

      {!isLoading && (
        <Stack
          height="100%"
          overflow="hidden"
          p={supportAgentInAgenticCenter ? 2 : 4}
          pl={isCollapsed ? 2 : PAGE_SIDE_PADDING + 'px'}
          pr={isCollapsed ? 2 : PAGE_SIDE_PADDING + 'px'}
          width="100%"
        >
          <AiAssistantPageHeader
            isCollapsed={isCollapsed}
            withFilters={withFilters}
            setDateFilters={setDateFilters}
            datePickerRef={datePickerRef}
            setAiAssistantDialogOpen={setAiAssistantDialogOpen}
            goBackCb={goBackCb}
            handleClosePanel={handleClosePanel}
          />

          {isEmpty && isManagedPackagedInstalled && (
            <AiAssistantEmptyState
              setAiAssistantDialogOpen={setAiAssistantDialogOpen}
              permissionString={permissionString}
            />
          )}

          {!isManagedPackagedInstalled && (
            <Stack sx={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <NoManagedPackageError actionType={ActionsRequireMp.AI} />
            </Stack>
          )}

          {isManagedPackagedInstalled && isInitialized && (
            <>
              <AiAssistantPerformance
                tableData={tableData ?? []}
                startDate={dateFilters.startTime ?? ''}
                endDate={dateFilters.endTime ?? ''}
                isCollapsed={isCollapsed}
              />
              <AiAssistantConversations
                crmOrgId={defaultCrmOrgId ?? ''}
                dateFilters={dateFilters}
                items={tableData ?? []}
                setConversationItem={openPreview}
                selectedConversation={selectedConversation}
                isCollapsed={isCollapsed}
              />
            </>
          )}
        </Stack>
      )}

      {isAiAssistantDialogOpen && (
        <AppsConnectionDialog
          closeDialog={(e, isRefresh) => {
            setAiAssistantDialogOpen(false);
            if (isRefresh) {
              fetchAiAssistentConversations(defaultCrmOrgId ?? '');
            }
          }}
        />
      )}

      {!!selectedConversation && (
        <AiAssistantConversationPreview
          conversationItem={selectedConversation}
          isPanelVisible={isPanelVisible}
          handleClosePanel={handleClosePreview}
          isCollapsed={isCollapsed}
        />
      )}
    </Stack>
  );
};

export default AiAssistantPage;
